/**
 * Service Provider Profile Details Wrapper (Feature 022)
 *
 * Wraps the public ProviderDetails view for display in the SP dashboard.
 * Shows the profile exactly as clients/FAs see it, without action buttons.
 */

import { Card } from '@/shared/components/ui/card';
import { ProviderDetailsContent } from '@/features/provider-directory/components/ProviderDetailsContent';
import {
  useProviderBase,
  useProviderServices,
  useProviderQualifications,
  useProviderMedia,
  useProviderReviews,
  useProviderReviewsSummary,
} from '@/features/provider-directory/hooks/useProviderSearch';
import { useProviderResources } from '@/features/provider-directory/hooks/useProviderResources';
import { SkeletonLoader } from '@/shared/components/SkeletonLoader';
import type { ServiceProviderProfile } from '../../api/profileApi';

type SPProviderDetailsWrapperProps = {
  profile: ServiceProviderProfile;
};

export function SPProviderDetailsWrapper({ profile }: SPProviderDetailsWrapperProps) {
  const providerId = profile.id;

  // Load all profile data - same as public profile for consistency
  const { data: provider, isLoading, isError } = useProviderBase(providerId, true);
  const { data: servicesData, isLoading: servicesLoading } = useProviderServices(providerId, true);
  const { data: qualificationsData, isLoading: qualificationsLoading } = useProviderQualifications(providerId, true);
  const { data: mediaData, isLoading: mediaLoading } = useProviderMedia(providerId, true);
  const { data: reviewsSummaryData, isLoading: reviewsSummaryLoading } = useProviderReviewsSummary(providerId, true);
  const { data: reviewsData, isLoading: reviewsLoading, isError: reviewsError } = useProviderReviews(providerId, true);
  // Resources are keyed by the author's User id (author_pro_id), not the
  // ServiceProvider id, so query by the provider's user id.
  const { data: resourcesData, isLoading: resourcesLoading } = useProviderResources(
    provider?.user.id,
    !!provider?.user.id,
  );

  if (isLoading) {
    return (
      <Card className="p-6">
        <div className="space-y-4">
          <SkeletonLoader variant="text" width="60%" height="32px" />
          <SkeletonLoader variant="text" count={3} />
          <SkeletonLoader variant="image" aspectRatio="16/9" />
        </div>
      </Card>
    );
  }

  if (isError || !provider) {
    return (
      <Card className="p-6">
        <p className="text-red-600">Error loading profile preview. Please try refreshing the page.</p>
      </Card>
    );
  }

  return (
    <ProviderDetailsContent
      provider={provider}
      isLoading={isLoading}
      servicesData={servicesData}
      servicesLoading={servicesLoading}
      qualificationsData={qualificationsData}
      qualificationsLoading={qualificationsLoading}
      mediaData={mediaData}
      mediaLoading={mediaLoading}
      reviewsData={reviewsData}
      reviewsLoading={reviewsLoading}
      reviewsError={reviewsError}
      resourcesData={resourcesData}
      resourcesLoading={resourcesLoading}
      displayRating={reviewsSummaryData?.averageRating ?? provider.averageRating ?? null}
      displayReviewCount={reviewsSummaryData?.totalReviewCount ?? provider.totalReviewCount ?? 0}
      reviewsSummaryLoading={reviewsSummaryLoading}
      // Preview mode: no action buttons
      canFavorite={false}
      canAddToNetwork={false}
      canReferProvider={false}
      currentFavoriteState={false}
      currentNetworkState={false}
      isFavoriteLoading={false}
      isNetworkLoading={false}
    />
  );
}
